import { toLocalStorage, fromLocalStorage } from './localstorage'
import Keycloak from 'keycloak-js'

const TOKEN_KEY = 'tokens'
const PROFILE_KEY = 'profile'

const silentCheckSsoRedirectUri =
  window.location.origin + '/silent-callback.html'

export type KeycloakEvent =
  | 'authSuccess'
  | 'refreshSuccess'
  | 'refreshError'
  | 'tokenExpired'
  | 'logout'

interface IKeycloakSettings {
  idpHint: string
  url: string
  realm: string
  clientId: string
  saveTokensToStorage: boolean
}

export default class KeycloakAdapter {
  keycloak: any
  idpHint: string
  saveTokensToStorage: boolean
  listeners: Record<KeycloakEvent, ((...args: any[]) => any)[]> = {
    authSuccess: [],
    refreshSuccess: [],
    refreshError: [],
    tokenExpired: [],
    logout: [],
  }

  constructor(settings: IKeycloakSettings) {
    const { saveTokensToStorage, ...keycloakSettings } = settings
    this.saveTokensToStorage = saveTokensToStorage
    this.keycloak = new Keycloak(keycloakSettings)
    this.idpHint = settings.idpHint
  }

  init({ expectedRole }: { expectedRole: string }) {
    const isOnline = window.navigator.onLine

    if (!isOnline && this.saveTokensToStorage) {
      const tokens = fromLocalStorage('tokens')
      if (tokens) {
        return Promise.resolve({ isAuthenticated: true, hasRealmAccess: true })
      }
      return Promise.resolve({ isAuthenticated: false, hasRealmAccess: false })
    }

    this._setupListeners()
    return this.keycloak
      .init({
        onLoad: 'check-sso',
        checkLoginIframe: false,
        silentCheckSsoRedirectUri,
      })
      .then((isAuthenticated) => {
        const hasRealmAccess = this.keycloak.hasRealmRole(expectedRole)
        if (hasRealmAccess) {
          this._saveTokens()
        }

        return {
          isAuthenticated,
          hasRealmAccess,
        }
      })
  }

  reset() {
    this.listeners = {
      authSuccess: [],
      logout: [],
      refreshError: [],
      tokenExpired: [],
      refreshSuccess: [],
    }
  }

  addListener(event: KeycloakEvent, closure) {
    this.listeners[event].push(closure)
  }

  redirectToLogin(redirectUri: string, extraScopes = []) {
    const options = {
      redirectUri,
      scope: 'profile' + extraScopes.join(' '),
      idpHint: this.idpHint,
    }
    return this.keycloak.login(options)
  }

  getToken(): Promise<string> {
    const isOnline = window.navigator.onLine
    if (!isOnline && this.saveTokensToStorage) {
      return Promise.resolve(this._loadTokens())
    }
    return this.updateToken()
      .then((hasUpdated: boolean) => {
        if (hasUpdated) {
          this._saveTokens()
        }
        return this.keycloak.token
      })
      .catch((err) => {
        throw new Error('Kunne ikke oppdatere token fra keycloak ' + err)
      })
  }

  updateToken(): Promise<boolean> {
    return this.keycloak.updateToken(30)
  }

  loadUserProfile() {
    const isOnline = window.navigator.onLine
    if (!isOnline && this.saveTokensToStorage) {
      const localProfile = fromLocalStorage('profile')
      if (!localProfile) {
        throw new Error(
          'loadUserProfile called while offline, and without a locally saved profile, this should not happen'
        )
      }
      return Promise.resolve(localProfile)
    }
    return this.keycloak.loadUserProfile().then((profile) => {
      this._saveProfile(profile)
      return profile
    })
  }

  // OBS Dette bruker window.location for å redirect
  logout(redirectUri = '/') {
    this._clearLocalStorage()
    this.keycloak.logout({
      redirectUri: `${window.location.origin}/${redirectUri}`,
    })
    this.reset()
  }

  _clearLocalStorage() {
    if (this.saveTokensToStorage) {
      toLocalStorage(TOKEN_KEY, undefined)
      toLocalStorage(PROFILE_KEY, undefined)
    }
  }

  _saveProfile(profile) {
    if (this.saveTokensToStorage) {
      toLocalStorage(PROFILE_KEY, profile)
    }
  }

  _loadProfile() {
    return fromLocalStorage(PROFILE_KEY)
  }

  _saveTokens() {
    if (this.saveTokensToStorage) {
      const accessToken = this.keycloak.token
      const refreshToken = this.keycloak.refreshToken
      toLocalStorage(TOKEN_KEY, { accessToken, refreshToken })
    }
  }

  _loadTokens() {
    return fromLocalStorage(TOKEN_KEY)
  }

  _setupListeners() {
    this.keycloak.onAuthSuccess = () => {
      this._saveTokens()
      const accessToken = this.keycloak.token
      const refreshToken = this.keycloak.refreshToken
      this.listeners['authSuccess'].forEach((c) =>
        c({ accessToken, refreshToken })
      )
    }

    this.keycloak.onAuthLogout = () => {
      this._clearLocalStorage()
      this.listeners['logout'].forEach((c) => c())
    }

    this.keycloak.onAuthRefreshError = (e) => {
      this._clearLocalStorage()
      this.listeners['refreshError'].forEach((c) => c(e))
    }

    this.keycloak.onTokenExpired = () => {
      this.listeners['tokenExpired'].forEach((c) => c())
    }

    this.keycloak.onAuthRefreshSuccess = () => {
      this._saveTokens()
      const accessToken = this.keycloak.token
      const refreshToken = this.keycloak.refreshToken
      this.listeners['refreshSuccess'].forEach((c) =>
        c({ accessToken, refreshToken })
      )
    }
  }
}
